/**
 * VG 2.0 · Phase 7 — EVA client.
 *
 * M8.3 hotfix: the EVA client used a bare `fetch` with a stale header
 * name (`X-Anon-Session-Id`) that the backend (`X-VG2-Anon-Token`)
 * never recognised. On an anonymous journey the very first call to
 * `/eva/session` therefore returned 401 and the owner got stuck on the
 * generic ErrorPanel. This module now routes through `vg2AnonAxios`,
 * matching every other VG2 helper (attach + auto-refresh on 401).
 */
import { createAnonAxios } from "./vg2AnonAxios";

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const api = createAnonAxios(API_V2);

function _opId(prefix, caseId) {
  // Persist a stable op id per (caseId, prefix) for idempotency across reloads.
  const key = `vg2-eva-${prefix}-${caseId}`;
  let op = window.localStorage.getItem(key);
  if (!op) {
    op = `${prefix}-${caseId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    window.localStorage.setItem(key, op);
  }
  return op;
}

function _mapError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const code = (detail && detail.error_code) || null;
  const message = (detail && detail.message)
    || e?.response?.statusText
    || e?.message
    || "errore imprevisto";
  const err = new Error(message);
  err.status = status;
  err.code = code;
  return err;
}

export async function startEvaSession({ caseId }) {
  try {
    const op = _opId("start", caseId);
    return (await api.post(`/cases/${caseId}/eva/session`, {
      operation_id: op,
    })).data;
  } catch (e) { throw _mapError(e); }
}

export async function getEvaSession({ caseId }) {
  try {
    return (await api.get(`/cases/${caseId}/eva/session`)).data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw _mapError(e);
  }
}

export async function postEvaMessage({ caseId, sessionId, content, turnIndex }) {
  try {
    const op = `msg-${caseId}-${sessionId}-${turnIndex}`;
    return (await api.post(`/cases/${caseId}/eva/messages`, {
      session_id: sessionId, content, operation_id: op,
    })).data;
  } catch (e) { throw _mapError(e); }
}

export async function closeEvaSession({ caseId, sessionId }) {
  try {
    return (await api.post(`/cases/${caseId}/eva/close`, {
      session_id: sessionId,
    })).data;
  } catch (e) { throw _mapError(e); }
}

export async function getPaHandoff({ caseId }) {
  try {
    return (await api.get(`/cases/${caseId}/pa-handoff`)).data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw _mapError(e);
  }
}
